import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardDescription, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { LogOut, Package, Clock, CheckCircle, ShoppingCart, UserPlus, Users, Key, Plus, Sparkles, Download, RefreshCw, Loader2, Menu, X, Home, FileText, Wrench, FileSpreadsheet, DollarSign, Truck, LineChart as LineChartIcon } from "lucide-react";
import OrdersList from "../components/OrdersList";
import OrderFormMultiPart from "../components/OrderFormMultiPart";
import CreateTechForm from "../components/CreateTechForm";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import ResetPasswordDialog from "../components/ResetPasswordDialog";
import DiagnosticGenerator from "../components/DiagnosticGenerator";
import PartCostComparison from "../components/PartCostComparison";
import SupplierSettings from "../components/SupplierSettings";
import TruckInventory from "../components/TruckInventory";
import FinanceReport from "../components/FinanceReport";

export default function ManagerDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [techs, setTechs] = useState([]);
  const [activeTab, setActiveTab] = useState("orders");
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ full_name: "", email: "", telegram_id: "", part_sources: [] });
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Default part sources template
  const DEFAULT_PART_SOURCES = [
    { id: "amazon", name: "Amazon", enabled: true },
    { id: "marcone", name: "Marcone", enabled: true },
    { id: "truck_part", name: "Truck Part", enabled: true },
    { id: "outsources", name: "Outsources", enabled: true },
    { id: "part_not_found", name: "Part not found", enabled: true },
  ];

  // Role-based permissions
  const isAdmin = user?.role === 'admin';
  const isPartManager = user?.role === 'part_manager';
  const isDispatcher = user?.role === 'dispatcher';
  const canManageUsers = isAdmin;
  const canCreateOrders = isAdmin || isPartManager || isDispatcher;
  const canEditOrders = isAdmin || isPartManager;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load all data in parallel for faster loading
        const [ordersRes, statsRes, techsRes] = await Promise.all([
          axios.get("/orders?limit=200", { timeout: 30000 }).catch(e => ({ data: [] })),
          axios.get("/stats/dashboard", { timeout: 15000 }).catch(e => ({ data: { total: 0, pending: 0, ordered: 0, canceled: 0 } })),
          axios.get("/auth/users", { timeout: 15000 }).catch(e => ({ data: [] }))
        ]);
        
        setOrders(ordersRes.data || []);
        setStats(statsRes.data || { total: 0, pending: 0, ordered: 0, canceled: 0 });
        setTechs(techsRes.data?.filter(u => u.role === "tech" || u.role === "admin" || u.role === "part_manager" || u.role === "dispatcher") || []);
      } catch (error) {
        console.error("Failed to load data:", error);
        toast.error("Connection issue - please refresh");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get("/orders");
      setOrders(response.data || []);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      toast.error("Failed to fetch orders");
    }
  };

  const refreshAll = async () => {
    try {
      setLoading(true);
      const [ordersRes, statsRes] = await Promise.all([
        axios.get("/orders"),
        axios.get("/stats/dashboard")
      ]);
      setOrders(ordersRes.data || []);
      setStats(statsRes.data || { total: 0, pending: 0, ordered: 0, canceled: 0 });
      setRefreshKey(Date.now());
      toast.success("Data refreshed");
    } catch (error) {
      console.error("Failed to refresh:", error);
      toast.error("Refresh failed - please try again");
    } finally {
      setLoading(false);
    }
  };

  const fetchTechs = async () => {
    try {
      const response = await axios.get("/auth/users");
      setTechs(response.data?.filter(u => u.role === "tech" || u.role === "admin" || u.role === "part_manager" || u.role === "dispatcher") || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleStatusUpdate = async (orderId, status) => {
    try {
      await axios.patch(`/orders/${orderId}`, { status });
      toast.success("Order status updated");
      refreshAll();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleUpdateUser = async (userId, updateData) => {
    try {
      await axios.put(`/auth/users/${userId}`, updateData);
      toast.success("User updated successfully");
      fetchTechs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const orderMatchesStatus = (order, filterStatus) => {
    if (filterStatus === "all") return true;
    const targetStatus = filterStatus === "returned" ? "return" : filterStatus;
    return order.status === targetStatus;
  };

  const exportToExcel = async () => {
    try {
      toast.loading("Fetching all orders...", { id: "export" });

      // Pull EVERY order from the server (bypasses the 200-row pagination cap)
      let allOrders = [];
      try {
        const res = await axios.get("/orders/export/all", { timeout: 60000 });
        allOrders = res.data || [];
      } catch (err) {
        // Fallback to whatever's already loaded if server endpoint fails
        console.error("export/all failed, falling back to loaded orders:", err);
        allOrders = orders;
      }

      // Flatten multi-part orders into one row per part
      const flattened = [];
      allOrders.forEach((order) => {
        const baseRow = {
          'Job Number': order.job_number,
          'Status': order.status || 'pending',
          'Assigned Tech': order.assigned_tech_name || 'Unassigned',
          'Created By': order.created_by_name || '',
          'ETA': order.part_eta || '',
          'Tracking Number': order.tracking_number || '',
          'Notes': order.notes || '',
          'Created At': order.created_at ? new Date(order.created_at).toLocaleDateString() : ''
        };

        if (Array.isArray(order.parts) && order.parts.length > 0) {
          order.parts.forEach((part, idx) => {
            flattened.push({
              ...baseRow,
              'Part #': idx + 1,
              'Part Number': part.part_number || 'N/A',
              'Brand': part.brand || '',
              'Part Source': part.part_source || 'N/A',
              'Quantity': part.quantity || 1,
              'Part Cost': part.part_cost || 0,
              'Total Price': part.calculated_price || 0,
            });
          });
        } else {
          flattened.push({
            ...baseRow,
            'Part #': 1,
            'Part Number': order.part_number || 'N/A',
            'Brand': order.brand || '',
            'Part Source': order.part_source || 'N/A',
            'Quantity': order.quantity || 1,
            'Part Cost': order.part_cost || 0,
            'Total Price': order.calculated_price || 0,
          });
        }
      });

      if (flattened.length === 0) {
        toast.error("No orders to export", { id: "export" });
        return;
      }

      const headers = [
        'Job Number', 'Part #', 'Part Number', 'Brand', 'Part Source', 'Quantity',
        'Part Cost', 'Total Price', 'Status', 'Assigned Tech', 'Created By',
        'ETA', 'Tracking Number', 'Notes', 'Created At'
      ];
      const csvContent = [
        headers.join(','),
        ...flattened.map(row =>
          headers.map(header => {
            const value = row[header];
            const stringValue = String(value === undefined || value === null ? '' : value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `orders_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${allOrders.length} orders (${flattened.length} rows)`, { id: "export" });
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export", { id: "export" });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Mobile Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-violet-600" />
              <div>
                <h1 className="text-lg font-bold text-slate-900">Parts Manager</h1>
                <p className="text-xs text-slate-500">{user.full_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={refreshAll}
                disabled={loading}
                className="p-2"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border-b shadow-lg z-50">
            <div className="p-4 space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => { setPasswordDialogOpen(true); setMobileMenuOpen(false); }}
              >
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={exportToExcel}
              >
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-red-600"
                onClick={onLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Quick Stats Bar - Mobile Optimized */}
      {stats && (
        <div className="bg-white border-b px-4 py-3">
          <div className="flex justify-between gap-2 overflow-x-auto">
            <button
              onClick={() => { setStatusFilter("all"); setActiveTab("orders"); }}
              className={`flex-1 min-w-[70px] py-2 px-3 rounded-lg text-center transition-all ${
                statusFilter === "all" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              <div className="text-lg font-bold">{stats.total_orders || 0}</div>
              <div className="text-xs">All</div>
            </button>
            <button
              onClick={() => { setStatusFilter("pending"); setActiveTab("orders"); }}
              className={`flex-1 min-w-[70px] py-2 px-3 rounded-lg text-center transition-all ${
                statusFilter === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              <div className="text-lg font-bold">{stats.pending_orders || 0}</div>
              <div className="text-xs">Pending</div>
            </button>
            <button
              onClick={() => { setStatusFilter("ordered"); setActiveTab("orders"); }}
              className={`flex-1 min-w-[70px] py-2 px-3 rounded-lg text-center transition-all ${
                statusFilter === "ordered" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              <div className="text-lg font-bold">{stats.ordered_orders || 0}</div>
              <div className="text-xs">Ordered</div>
            </button>
            <button
              onClick={() => { setStatusFilter("canceled"); setActiveTab("orders"); }}
              className={`flex-1 min-w-[70px] py-2 px-3 rounded-lg text-center transition-all ${
                statusFilter === "canceled" ? "bg-gray-200 text-gray-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              <div className="text-lg font-bold">{stats.canceled_orders || 0}</div>
              <div className="text-xs">Canceled</div>
            </button>
          </div>
        </div>
      )}

      {/* Main Content - with proper scrolling and bottom padding for fixed nav */}
      <main className="flex-1 px-4 py-4 pb-24 overflow-y-auto">
        {/* Orders Tab */}
        {activeTab === "orders" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {statusFilter === "all" ? "All Orders" : 
                 statusFilter === "pending" ? "Pending" :
                 statusFilter === "ordered" ? "Ordered" :
                 statusFilter === "canceled" ? "Canceled" : "Orders"}
              </h2>
              {loading && <Loader2 className="w-5 h-5 animate-spin text-violet-600" />}
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
                <span className="ml-3 text-slate-600">Loading...</span>
              </div>
            ) : (
              <OrdersList 
                key={`orders-${refreshKey}`}
                orders={orders.filter(order => orderMatchesStatus(order, statusFilter))} 
                onRefresh={refreshAll} 
                user={user} 
                onStatusUpdate={handleStatusUpdate} 
              />
            )}
          </div>
        )}

        {/* Create Order Tab */}
        {activeTab === "create" && (
          <div>
            <h2 className="text-lg font-semibold mb-4">New Order</h2>
            <OrderFormMultiPart 
              onOrderCreated={() => { fetchOrders(); setActiveTab("orders"); }} 
              user={user} 
            />
          </div>
        )}

        {/* AI Diagnostic Tab */}
        {activeTab === "diagnostic" && (
          <div>
            <h2 className="text-lg font-semibold mb-4">AI Diagnostic</h2>
            <DiagnosticGenerator />
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="pb-8">
            <h2 className="text-lg font-semibold mb-4">Manage Users ({techs.length})</h2>
            <div className="space-y-3">
              {techs.map((tech) => (
                <Card key={tech.id} className="p-4">
                  {editingUser === tech.id ? (
                    /* Edit Mode */
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-slate-600">Full Name</label>
                        <input
                          type="text"
                          value={editFormData.full_name}
                          onChange={(e) => setEditFormData({...editFormData, full_name: e.target.value})}
                          className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600">Email</label>
                        <input
                          type="email"
                          value={editFormData.email}
                          onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                          className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600">Telegram Chat ID</label>
                        <input
                          type="text"
                          value={editFormData.telegram_id}
                          onChange={(e) => setEditFormData({...editFormData, telegram_id: e.target.value})}
                          placeholder="e.g., 123456789"
                          className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                        />
                        <p className="text-xs text-slate-400 mt-1">Tech will receive Telegram notifications when parts are ordered</p>
                      </div>
                      
                      {/* Part Sources for this user */}
                      <div className="pt-2 border-t">
                        <label className="text-xs font-medium text-slate-600 block mb-2">Part Sources (visible to this user)</label>
                        <div className="grid grid-cols-2 gap-2">
                          {editFormData.part_sources.map((source) => (
                            <label key={source.id} className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={source.enabled}
                                onChange={() => {
                                  setEditFormData(prev => ({
                                    ...prev,
                                    part_sources: prev.part_sources.map(s =>
                                      s.id === source.id ? { ...s, enabled: !s.enabled } : s
                                    )
                                  }));
                                }}
                                className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                              />
                              <span className={source.enabled ? "text-slate-700" : "text-slate-400"}>{source.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            handleUpdateUser(tech.id, editFormData);
                            setEditingUser(null);
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingUser(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{tech.full_name}</p>
                        <p className="text-sm text-slate-500">{tech.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                            tech.role === 'admin' ? 'bg-violet-100 text-violet-700' :
                            tech.role === 'part_manager' ? 'bg-blue-100 text-blue-700' :
                            tech.role === 'dispatcher' ? 'bg-green-100 text-green-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {tech.role}
                          </span>
                          {tech.telegram_id ? (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-blue-50 text-blue-600">
                              📱 TG: {tech.telegram_id}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-slate-50 text-slate-400">
                              No Telegram
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingUser(tech.id);
                            // Load user's part sources or use defaults
                            const userSources = tech.part_sources || DEFAULT_PART_SOURCES;
                            setEditFormData({
                              full_name: tech.full_name || "",
                              email: tech.email || "",
                              telegram_id: tech.telegram_id || "",
                              part_sources: userSources
                            });
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(tech);
                            setResetPasswordDialogOpen(true);
                          }}
                        >
                          Reset PW
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Create User Tab */}
        {activeTab === "create-user" && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Create User</h2>
            <CreateTechForm onTechCreated={fetchTechs} />
          </div>
        )}

        {/* Part Cost Comparison Tab - Admin Only */}
        {activeTab === "compare" && user?.role === "admin" && (
          <PartCostComparison />
        )}

        {/* Supplier Pricing Settings - Admin Only */}
        {activeTab === "suppliers" && user?.role === "admin" && (
          <SupplierSettings />
        )}

        {/* Truck Inventory - All roles */}
        {activeTab === "truck" && (
          <TruckInventory user={user} />
        )}

        {/* Finance - Admin only */}
        {activeTab === "finance" && user?.role === "admin" && (
          <FinanceReport />
        )}
      </main>

      {/* Bottom Navigation - Mobile Optimized */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-50 safe-area-inset-bottom">
        <div className="flex justify-around items-center py-2 px-1">
          <button
            onClick={() => setActiveTab("orders")}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              activeTab === "orders" ? "text-violet-600 bg-violet-50" : "text-slate-500"
            }`}
          >
            <Home className="w-5 h-5" />
            <span className="text-[10px] mt-1">Orders</span>
          </button>
          
          {canCreateOrders && (
            <button
              onClick={() => setActiveTab("create")}
              className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
                activeTab === "create" ? "text-violet-600 bg-violet-50" : "text-slate-500"
              }`}
              data-testid="nav-new"
            >
              <Plus className="w-5 h-5" />
              <span className="text-[10px] mt-1">New</span>
            </button>
          )}
          
          {/* AI Tool - Available to ALL users */}
          <button
            onClick={() => setActiveTab("diagnostic")}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              activeTab === "diagnostic" ? "text-violet-600 bg-violet-50" : "text-slate-500"
            }`}
          >
            <Sparkles className="w-5 h-5" />
            <span className="text-[10px] mt-1">AI</span>
          </button>
          
          {canManageUsers && (
            <>
              <button
                onClick={() => setActiveTab("users")}
                className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
                  activeTab === "users" ? "text-violet-600 bg-violet-50" : "text-slate-500"
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="text-[10px] mt-1">Users</span>
              </button>
              <button
                onClick={() => setActiveTab("create-user")}
                className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
                  activeTab === "create-user" ? "text-violet-600 bg-violet-50" : "text-slate-500"
                }`}
              >
                <UserPlus className="w-5 h-5" />
                <span className="text-[10px] mt-1">Add</span>
              </button>
            </>
          )}

          {/* Compare Tab - Admin Only */}
          {user?.role === "admin" && (
            <button
              onClick={() => setActiveTab("compare")}
              className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
                activeTab === "compare" ? "text-violet-600 bg-violet-50" : "text-slate-500"
              }`}
            >
              <FileSpreadsheet className="w-5 h-5" />
              <span className="text-[10px] mt-1">Compare</span>
            </button>
          )}

          {/* Supplier Pricing - Admin Only */}
          {user?.role === "admin" && (
            <button
              onClick={() => setActiveTab("suppliers")}
              className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
                activeTab === "suppliers" ? "text-violet-600 bg-violet-50" : "text-slate-500"
              }`}
              data-testid="nav-suppliers"
            >
              <DollarSign className="w-5 h-5" />
              <span className="text-[10px] mt-1">Pricing</span>
            </button>
          )}

          {/* Truck Inventory - All roles */}
          <button
            onClick={() => setActiveTab("truck")}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              activeTab === "truck" ? "text-violet-600 bg-violet-50" : "text-slate-500"
            }`}
            data-testid="nav-truck"
          >
            <Truck className="w-5 h-5" />
            <span className="text-[10px] mt-1">Truck</span>
          </button>

          {/* Finance — Admin only */}
          {user?.role === "admin" && (
            <button
              onClick={() => setActiveTab("finance")}
              className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
                activeTab === "finance" ? "text-violet-600 bg-violet-50" : "text-slate-500"
              }`}
              data-testid="nav-finance"
            >
              <LineChartIcon className="w-5 h-5" />
              <span className="text-[10px] mt-1">Finance</span>
            </button>
          )}
        </div>
      </nav>

      {/* Dialogs */}
      <ChangePasswordDialog
        open={passwordDialogOpen}
        onOpenChange={setPasswordDialogOpen}
      />
      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        onOpenChange={setResetPasswordDialogOpen}
        user={selectedUser}
      />
    </div>
  );
}
